const FIRST_TOUCH_STORAGE_KEY = 'bab:booking-attribution:first-touch';
const LAST_TOUCH_STORAGE_KEY = 'bab:booking-attribution:last-touch';
const ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const isBrowser = typeof window !== 'undefined';

export const SELF_REPORTED_ATTRIBUTION_OPTIONS = [
  { value: 'google_search', label: 'Google search' },
  { value: 'build_a_bear_website', label: 'Build-A-Bear website' },
  { value: 'facebook_instagram', label: 'Facebook / Instagram' },
  { value: 'email', label: 'Email' },
  { value: 'friend_family', label: 'Friend / family' },
  { value: 'school_partner', label: 'School / partner' },
  { value: 'returning_customer', label: 'Returning customer' },
  { value: 'other', label: 'Other' },
];

const readStorage = (storage, key) => {
  if (!storage) return null;
  try {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (_error) {
    return null;
  }
};

const writeStorage = (storage, key, value) => {
  if (!storage) return;
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch (_error) {
    // Ignore storage failures so attribution never blocks bookings.
  }
};

const sanitizeString = (value, maxLength = 200) => {
  if (typeof value !== 'string') return '';
  return value.trim().slice(0, maxLength);
};

const ATTRIBUTION_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'fbclid',
  'msclkid',
];

const normalizeLandingPath = () => {
  if (!isBrowser) return '';
  const { pathname = '/', search = '' } = window.location;
  const params = new URLSearchParams(search);
  const allowedParams = new URLSearchParams();
  for (const key of ATTRIBUTION_PARAMS) {
    const value = params.get(key);
    if (value !== null) {
      allowedParams.set(key, value);
    }
  }
  const queryString = allowedParams.toString();
  return sanitizeString(`${pathname}${queryString ? `?${queryString}` : ''}`, 300);
};

const normalizeReferrerHost = () => {
  if (!isBrowser || !document.referrer) return '';
  try {
    return sanitizeString(new URL(document.referrer).hostname.replace(/^www\./, '').toLowerCase(), 160);
  } catch (_error) {
    return '';
  }
};

const getSearchParam = (params, key) => sanitizeString(params.get(key), 160);

const buildTouchSnapshot = () => {
  if (!isBrowser) return null;
  const params = new URLSearchParams(window.location.search);
  return {
    source: getSearchParam(params, 'utm_source'),
    medium: getSearchParam(params, 'utm_medium'),
    campaign: getSearchParam(params, 'utm_campaign'),
    content: getSearchParam(params, 'utm_content'),
    term: getSearchParam(params, 'utm_term'),
    landingPath: normalizeLandingPath(),
    referrerHost: normalizeReferrerHost(),
    clickIds: {
      gclid: getSearchParam(params, 'gclid'),
      fbclid: getSearchParam(params, 'fbclid'),
      msclkid: getSearchParam(params, 'msclkid'),
    },
    capturedAt: new Date().toISOString(),
  };
};

const hasAttributionSignal = (touch) =>
  Boolean(
    touch &&
      (touch.source ||
        touch.medium ||
        touch.campaign ||
        touch.content ||
        touch.term ||
        touch.referrerHost ||
        touch.clickIds?.gclid ||
        touch.clickIds?.fbclid ||
        touch.clickIds?.msclkid),
  );

const isExpired = (value) => {
  const capturedAt = value?.capturedAt ? new Date(value.capturedAt).getTime() : Number.NaN;
  if (Number.isNaN(capturedAt)) return true;
  return Date.now() - capturedAt > ATTRIBUTION_TTL_MS;
};

export const initBookingAttribution = () => {
  if (!isBrowser) return;

  const touch = buildTouchSnapshot();
  if (!hasAttributionSignal(touch)) return;

  const localStorage = window.localStorage;
  const sessionStorage = window.sessionStorage;
  const firstTouch = readStorage(localStorage, FIRST_TOUCH_STORAGE_KEY);
  if (!firstTouch || isExpired(firstTouch)) {
    writeStorage(localStorage, FIRST_TOUCH_STORAGE_KEY, touch);
  }
  writeStorage(sessionStorage, LAST_TOUCH_STORAGE_KEY, touch);
};

export const getBookingAttributionSnapshot = () => {
  if (!isBrowser) return null;
  const firstTouch = readStorage(window.localStorage, FIRST_TOUCH_STORAGE_KEY);
  const lastTouch = readStorage(window.sessionStorage, LAST_TOUCH_STORAGE_KEY);

  return {
    firstTouch: firstTouch && !isExpired(firstTouch) ? firstTouch : null,
    lastTouch: lastTouch || buildTouchSnapshot(),
  };
};

export const clearBookingAttributionForTests = () => {
  if (!isBrowser) return;
  window.localStorage.removeItem(FIRST_TOUCH_STORAGE_KEY);
  window.sessionStorage.removeItem(LAST_TOUCH_STORAGE_KEY);
};
